import React, { useState } from 'react';
import Button from '../Button/Button';
import LanguageDialog from '../LanguageDialog/LanguageDialog';
import ModalList from '../ModalList/ModalList';
import './LanguageSection.css';

interface LanguageSectionProps {
  languages: string[];
  onLanguagesChange: (languages: string[]) => void;
  onDeleteLanguage?: (language: string) => void;
  onReorderLanguages?: (newOrder: string[]) => void;
  draggable?: boolean;
  title?: string;
  description?: string;
  emptyMessage?: string;
  addButtonText?: string;
  isLoading?: boolean;
  canDelete?: boolean;
}

const LanguageSection: React.FC<LanguageSectionProps> = ({
  languages,
  onLanguagesChange,
  onDeleteLanguage,
  onReorderLanguages,
  draggable = false,
  title = "Project Languages",
  description,
  emptyMessage = "No languages added",
  addButtonText = "Add Language",
  isLoading = false,
  canDelete = true
}) => {
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false);

  const handleAddLanguage = (language: string) => {
    if (!languages.includes(language)) {
      onLanguagesChange([...languages, language]);
    }
    setIsLanguageDialogOpen(false);
  };

  const handleDeleteLanguage = (language: string) => {
    if (onDeleteLanguage) {
      onDeleteLanguage(language);
    } else if (canDelete && languages.length > 1) {
      onLanguagesChange(languages.filter(lang => lang !== language));
    }
  };

  const handleReorder = (newOrder: string[]) => {
    if (onReorderLanguages) {
      onReorderLanguages(newOrder);
    } else {
      onLanguagesChange(newOrder);
    }
  };

  return (
    <>
      <div className="language-section">
        <div className="section-header">
          <span className="section-title">{title}</span>
          {description && (
            <span className="section-description">{description}</span>
          )}
        </div>
        <ModalList 
          items={languages}
          onDeleteClick={languages.length > 1 ? handleDeleteLanguage : undefined}
          emptyMessage={emptyMessage}
          draggable={draggable}
          onReorder={handleReorder}
        />
        <Button
          variant="secondary"
          onClick={() => setIsLanguageDialogOpen(true)}
          size="small"
        >
          {addButtonText}
        </Button>
      </div>

      <LanguageDialog
        isOpen={isLanguageDialogOpen}
        onClose={() => setIsLanguageDialogOpen(false)}
        onSelect={handleAddLanguage}
        existingLanguages={languages}
        onAdd={handleAddLanguage}
        isLoading={isLoading}
      />
    </>
  );
};

export default LanguageSection;
